import { useRef, useState } from "react";
import { BookOpen, Sparkles, RefreshCw, Trash2, Mic, MessageCircleHeart, ImagePlus, Loader2, Send, MapPin, Palmtree, Pencil, Check, X, ChevronDown, Trophy, Film, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE, thumbUrl } from "../lib/api";
import { confirmDialog } from "./ConfirmHost";
import { compressImages } from "../lib/imageCompress";
import { runResumableUpload, UploadCancelled } from "../lib/uploadResume";
import { UploadPausedBanner } from "./UploadPausedBanner";
import { photoDate, photoGps, localYMD } from "../lib/photoDates";
import { SmartPhotoImport } from "./SmartPhotoImport";
import { StageShareButton } from "./StageShareButton";
import { getPositionIfGranted, isTripToday } from "../lib/geo";
import { useAuth } from "../context/AuthContext";
import { VoiceRecorder } from "./VoiceRecorder";
import { InviteCard } from "./InviteCard";
import { DayComments } from "./DayComments";
import { EntryReactions } from "./EntryReactions";
import { AllCommentsPanel } from "./AllCommentsPanel";
import { Lightbox } from "./Lightbox";
import { VideoEmbed } from "./VideoEmbed";
import { SmartImg } from "./SmartImg";
import { Button } from "./ui/button";
import { tNL } from "../lib/journalI18n";

const MAX_LOG_LEN = 5000;

const Removable = ({ id, onRemove, children }) => (
  <div className="relative">
    {children}
    {onRemove && (
      <button data-testid={`remove-photo-${id}`} type="button" onClick={() => onRemove(id)} title="Verwijder deze foto/video uit dit moment"
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-[background-color] duration-200">
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

const EntryMedia = ({ e, onRemove, mediaUrl, setLb, t }) => {
  const media = e.media || [];
  const images = media.filter((m) => m.content_type?.startsWith("image"));
  const videos = media.filter((m) => m.content_type?.startsWith("video"));
  const docs = media.filter((m) => m.content_type === "application/pdf");
  const ids = [...(e.photo_id ? [e.photo_id] : []), ...images.map((m) => m.id)];
  const openLb = (id) => setLb({ urls: ids.map(mediaUrl), index: Math.max(0, ids.indexOf(id)) });
  return (
    <>
      {e.photo_id && (
        <Removable id={e.photo_id} onRemove={onRemove}>
          <SmartImg src={thumbUrl(mediaUrl(e.photo_id))} retryLabel={t("photoRetry")} alt="Reislog foto" loading="lazy" decoding="async" onClick={() => openLb(e.photo_id)} className="rounded-xl mb-3 w-full max-h-64 object-cover cursor-zoom-in" />
        </Removable>
      )}
      {images.length > 0 && (
        <div className={`grid gap-2 mb-3 ${images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {images.map((m) => (
            <Removable key={m.id} id={m.id} onRemove={onRemove}>
              <SmartImg src={thumbUrl(mediaUrl(m.id))} retryLabel={t("photoRetry")} alt="Reisfoto" loading="lazy" decoding="async" onClick={() => openLb(m.id)} className="rounded-xl w-full max-h-64 object-cover cursor-zoom-in" />
            </Removable>
          ))}
        </div>
      )}
      {videos.map((m) => (
        <Removable key={m.id} id={m.id} onRemove={onRemove}>
          <video controls preload="metadata" className="rounded-xl mb-3 w-full max-h-72 bg-black" data-testid={`log-video-${m.id}`}>
            <source src={mediaUrl(m.id)} type={m.content_type} />
          </video>
        </Removable>
      ))}
      {docs.map((m) => (
        <Removable key={m.id} id={m.id} onRemove={onRemove}>
          <a href={mediaUrl(m.id)} target="_blank" rel="noreferrer" data-testid={`log-doc-${m.id}`}
            className="flex items-center gap-2.5 bg-secondary rounded-xl px-3 py-2.5 mb-3 hover:bg-secondary/70 transition-[background-color] duration-200">
            <FileText className="w-5 h-5 text-red-500 shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold truncate">{m.filename || "Document.pdf"}</span>
              <span className="block text-[11px] text-muted-foreground">PDF{m.size ? ` · ${m.size >= 1024 * 1024 ? `${(m.size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(m.size / 1024))} KB`}` : ""} · tik om te openen</span>
            </span>
          </a>
        </Removable>
      ))}
    </>
  );
};

const autoGrow = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
};

const DayComposer = ({ trip, day, onRefresh, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [progress, setProgress] = useState(null);
  const [paused, setPaused] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const taRef = useRef(null);

  const addFiles = (list) => {
    const files = Array.from(list || []).filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/") || f.type === "application/pdf" || f.name?.toLowerCase().endsWith(".pdf"));
    if (!files.length) return;
    setPending((p) => {
      const room = 30 - p.length;
      if (files.length > room) toast.info(`Maximaal 30 bestanden per moment${room > 0 ? ` — de eerste ${room} zijn toegevoegd` : ""}`);
      return [...p, ...files.slice(0, Math.max(0, room)).map((f) => ({ file: f, url: URL.createObjectURL(f), isVideo: f.type.startsWith("video/"), isPdf: f.type === "application/pdf" || f.name?.toLowerCase().endsWith(".pdf") }))];
    });
  };
  const removePending = (url) => setPending((p) => {
    p.find((x) => x.url === url) && URL.revokeObjectURL(url);
    return p.filter((x) => x.url !== url);
  });

  const post = async () => {
    if (!text.trim() && pending.length === 0) return;
    setSending(true);
    const us = { done: 0, total: 0, entryId: null };
    try {
      const pos = isTripToday(trip) ? await getPositionIfGranted() : null;
      if (pending.length > 0) {
        let files = pending.map((x) => x.file);
        const dated = await Promise.all(files.map(async (f) => ({ f, d: await photoDate(f) })));
        const mismatch = dated.filter((x) => x.d && localYMD(x.d) !== day.date);
        if (mismatch.length > 0) {
          const dates = [...new Set(mismatch.map((x) => x.d.toLocaleDateString("nl-BE", { day: "numeric", month: "short" })))].slice(0, 3).join(", ");
          if (mismatch.length === files.length) {
            const ok = await confirmDialog(`Geen van deze ${files.length > 1 ? `${files.length} bestanden` : "bestanden"} is op deze dag gemaakt (opnamedatum: ${dates}). Toch toevoegen aan dag ${day.day_number}?\n\nTip: met "Foto's slim importeren" bovenaan het reislog zetten we ze automatisch bij de juiste dag.`, { title: "Andere opnamedatum", confirmLabel: "Toch toevoegen" });
            if (!ok) return;
          } else {
            const all = await confirmDialog(`${mismatch.length} van de ${files.length} bestanden ${mismatch.length === 1 ? "is" : "zijn"} op een andere dag gemaakt (${dates}). Wil je ze tóch allemaal aan dag ${day.day_number} toevoegen?\n\nTip: met "Foto's slim importeren" bovenaan het reislog zetten we alles automatisch bij de juiste dag.`, { title: "Andere opnamedatum", confirmLabel: "Alles toch toevoegen", cancelLabel: `Alleen deze dag (${files.length - mismatch.length})` });
            if (!all) files = dated.filter((x) => !(x.d && localYMD(x.d) !== day.date)).map((x) => x.f);
          }
        }
        const compressed = await compressImages(files);
        us.total = compressed.length;
        us.files = files;
        const first = compressed.slice(0, 8);
        const fd = new FormData();
        first.forEach((f) => fd.append("files", f));
        fd.append("day_number", day.day_number);
        if (text.trim()) fd.append("caption", text.trim());
        if (pos) {
          fd.append("lat", pos.lat);
          fd.append("lon", pos.lon);
        } else {
          const pgps = (await Promise.all(files.slice(0, 10).map(photoGps))).find(Boolean);
          if (pgps) { fd.append("lat", pgps.lat); fd.append("lon", pgps.lon); }
        }
        if (compressed.length > 8) setProgress({ done: 0, total: compressed.length });
        const steps = [async () => {
          const { data: entry } = await api.post(`/trips/${trip.id}/media`, fd);
          us.entryId = entry.id;
          us.done = first.length;
          setProgress((p) => (p ? { ...p, done: us.done } : p));
        }];
        for (let i = 8; i < compressed.length; i += 8) {
          const batch = compressed.slice(i, i + 8);
          steps.push(async () => {
            const fd2 = new FormData();
            batch.forEach((f) => fd2.append("files", f));
            await api.post(`/trips/${trip.id}/log/${us.entryId}/photos`, fd2);
            us.done += batch.length;
            setProgress((p) => (p ? { ...p, done: us.done } : p));
          });
        }
        await runResumableUpload(steps, {
          onPaused: (p) => setPaused({ ...p, done: us.done, total: us.total }),
          onResumed: () => setPaused(null),
        });
        setProgress(null);
      } else {
        const fd = new FormData();
        fd.append("text", text.trim());
        fd.append("day_number", day.day_number);
        if (pos) { fd.append("lat", pos.lat); fd.append("lon", pos.lon); }
        await api.post(`/trips/${trip.id}/log`, fd);
      }
      toast.success(`Moment toegevoegd aan dag ${day.day_number}!`);
      pending.forEach((x) => URL.revokeObjectURL(x.url));
      setPending([]);
      setText("");
      if (taRef.current) taRef.current.style.height = "auto";
      onRefresh?.();
    } catch (err) {
      if (err instanceof UploadCancelled) {
        if (us.entryId) {
          const uploaded = new Set((us.files || []).slice(0, us.done));
          toast.info(`Upload gestopt — ${us.done} van ${us.total} bestanden staan in je reislog${us.done < us.total ? ", de rest staat nog klaar in het invoerveld" : ""}`);
          setPending((p) => p.filter((x) => {
            if (uploaded.has(x.file)) {
              URL.revokeObjectURL(x.url);
              return false;
            }
            return true;
          }));
          setText("");
          if (taRef.current) taRef.current.style.height = "auto";
          onRefresh?.();
        } else {
          toast.info("Upload gestopt — er is nog niets geplaatst, je bestanden staan nog klaar");
        }
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setSending(false);
      setProgress(null);
      setPaused(null);
    }
  };

  const dictate = async (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "dicteer.webm");
    try {
      const r = await api.post("/voice/transcribe", fd);
      setText((d) => (d.trim() ? `${d.trim()} ${r.data.text}` : r.data.text).slice(0, MAX_LOG_LEN));
      setTimeout(() => autoGrow(taRef.current), 50);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendVoice = async (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "log.webm");
    fd.append("day_number", day.day_number);
    const pos = isTripToday(trip) ? await getPositionIfGranted() : null;
    if (pos) { fd.append("lat", pos.lat); fd.append("lon", pos.lon); }
    try {
      const { data } = await api.post(`/trips/${trip.id}/log/voice`, fd);
      toast.success(`Toegevoegd aan dag ${day.day_number}!`, { description: data.text.slice(0, 120) });
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!open) {
    return (
      <button data-testid={`day-composer-open-${day.day_number}`} onClick={() => { setOpen(true); setTimeout(() => taRef.current?.focus(), 80); }}
        className="w-full mt-3 flex items-center gap-2.5 bg-secondary/70 hover:bg-secondary rounded-2xl px-4 py-3 text-sm text-muted-foreground text-left transition-[background-color] duration-200">
        <Pencil className="w-4 h-4 shrink-0 text-[#B5838D]" /> Schrijf iets over deze dag...
        <span className="ml-auto flex items-center gap-2 text-[#B5838D] shrink-0"><ImagePlus className="w-4 h-4" /><Mic className="w-4 h-4" /></span>
      </button>
    );
  }

  return (
    <div data-testid={`day-composer-${day.day_number}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
      className={`mt-3 bg-card rounded-2xl p-3.5 border transition-[border-color,box-shadow] duration-200 ${dragging ? "border-[#B5838D] ring-2 ring-[#B5838D]/30" : "border-border"}`}>
      <textarea ref={taRef} data-testid={`day-note-input-${day.day_number}`} value={text} maxLength={MAX_LOG_LEN} rows={4}
        onChange={(e) => { setText(e.target.value); autoGrow(e.target); }}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post(); }}
        onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); addFiles(e.clipboardData.files); } }}
        placeholder={`Vertel over deze dag — wat maakte hem bijzonder?\nSleep of plak gerust foto's en video's hierin.`}
        className="w-full bg-secondary/60 rounded-xl px-3.5 py-3 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-[#B5838D] resize-none overflow-y-auto transition-[box-shadow] duration-200" />
      {text.length > MAX_LOG_LEN * 0.8 && (
        <p data-testid={`composer-charcount-${day.day_number}`} className="text-right text-[10px] text-muted-foreground font-mono">{text.length}/{MAX_LOG_LEN}</p>
      )}
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid={`composer-previews-${day.day_number}`}>
          {pending.map((x) => (
            <div key={x.url} className="relative w-20 h-20">
              {x.isPdf ? (
                <span className="w-full h-full rounded-xl bg-red-500/10 border border-red-200 text-red-600 flex flex-col items-center justify-center gap-1 px-1">
                  <FileText className="w-5 h-5" />
                  <span className="text-[8px] truncate max-w-full">{x.file.name}</span>
                </span>
              ) : x.isVideo ? (
                <span className="w-full h-full rounded-xl bg-black/85 text-white flex flex-col items-center justify-center gap-1 px-1">
                  <Film className="w-5 h-5" />
                  <span className="text-[8px] truncate max-w-full">{x.file.name}</span>
                </span>
              ) : (
                <img src={x.url} alt="Preview" className="w-full h-full rounded-xl object-cover" />
              )}
              <button data-testid={`composer-remove-${x.file.name}`} type="button" onClick={() => removePending(x.url)} title="Verwijder uit dit moment"
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center transition-[background-color] duration-200">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-2.5">
        <UploadPausedBanner paused={paused} testId={`upload-paused-${day.day_number}`} />
        {progress && !paused && (
          <p data-testid={`composer-upload-progress-${day.day_number}`} className="w-full text-xs font-semibold text-primary font-mono">
            Uploaden {progress.done}/{progress.total} bestanden…
          </p>
        )}
        <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" multiple className="hidden" data-testid={`day-media-input-${day.day_number}`}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <button data-testid={`day-media-button-${day.day_number}`} type="button" onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1.5 rounded-full bg-secondary hover:bg-secondary/70 text-xs font-semibold px-3.5 py-2 transition-[background-color] duration-200">
          <ImagePlus className="w-3.5 h-3.5 text-[#B5838D]" /> Foto/video/PDF
        </button>
        <VoiceRecorder pill label="Dicteer" testId={`dictate-${day.day_number}`} onRecorded={dictate} />
        <VoiceRecorder pill label="Spreek moment in" testId={`voicelog-${day.day_number}`} onRecorded={sendVoice} />
        <span className="ml-auto flex items-center gap-1.5">
          <button data-testid={`day-composer-close-${day.day_number}`} type="button" onClick={() => setOpen(false)} title="Composer inklappen"
            className="p-2 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
            <ChevronDown className="w-4 h-4 rotate-180" />
          </button>
          <button data-testid={`day-note-send-${day.day_number}`} type="button" onClick={post} disabled={(!text.trim() && pending.length === 0) || sending}
            className="flex items-center gap-1.5 rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground text-xs font-bold px-5 py-2.5 disabled:opacity-40 active:scale-95 transition-[transform,background-color] duration-200">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Plaats moment
          </button>
        </span>
      </div>
      <p className="hidden sm:block text-[10px] text-muted-foreground mt-1.5">Enter = nieuwe regel · Ctrl+Enter = plaatsen · sleep of plak beelden rechtstreeks in het vak</p>
    </div>
  );
};

const EntryCard = ({ e, dayNum, tripId, reactions, commentReactions, user, t, tr, locale, readOnly, onRefresh, submitComment, deleteComment, reactComment, entryComments, mediaUrl, setLb }) => {
  const entryReactions = reactions.filter((r) => r.entry_id === e.id);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(e.text || "");
  const [draftTs, setDraftTs] = useState("");
  const [draftVideo, setDraftVideo] = useState(e.video?.url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [paused, setPaused] = useState(null);
  const fileRef = useRef(null);
  const addPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const us = { done: 0, total: 0 };
    try {
      await api.patch(`/trips/${tripId}/log/${e.id}`, { text: draft.trim(), timestamp: draftTs && draftTs !== toLocalInput(e.timestamp) ? new Date(draftTs).toISOString() : null });
      const list = [...files].slice(0, 30);
      if (files.length > 30) toast.info("Maximaal 30 bestanden per keer — de eerste 30 worden toegevoegd");
      us.total = list.length;
      const steps = [];
      for (let i = 0; i < list.length; i += 8) {
        const batch = list.slice(i, i + 8);
        steps.push(async () => {
          const fd = new FormData();
          batch.forEach((f) => fd.append("files", f));
          await api.post(`/trips/${tripId}/log/${e.id}/photos`, fd);
          us.done += batch.length;
        });
      }
      await runResumableUpload(steps, {
        onPaused: (p) => setPaused({ ...p, done: us.done, total: us.total }),
        onResumed: () => setPaused(null),
      });
      toast.success("Toegevoegd aan dit moment");
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      if (err instanceof UploadCancelled) {
        toast.info(us.done ? `Upload gestopt — ${us.done} van ${us.total} bestanden zijn toegevoegd` : "Upload gestopt");
        if (us.done) onRefresh?.();
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setUploading(false);
      setPaused(null);
    }
  };
  const dictate = async (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "dicteer.webm");
    try {
      const r = await api.post("/voice/transcribe", fd);
      setDraft((d) => (d.trim() ? `${d.trim()} ${r.data.text}` : r.data.text));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const toLocalInput = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      if (draftVideo.trim() !== (e.video?.url || "")) {
        await api.patch(`/trips/${tripId}/log/${e.id}/video`, { video_url: draftVideo.trim() || null });
      }
      const tsChanged = draftTs && draftTs !== toLocalInput(e.timestamp);
      if (draft.trim() !== (e.text || "") || tsChanged) {
        await api.patch(`/trips/${tripId}/log/${e.id}`, { text: draft.trim(), timestamp: tsChanged ? new Date(draftTs).toISOString() : null });
      }
      toast.success("Moment aangepast");
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const removePhoto = async (pid) => {
    if (!(await confirmDialog("Deze foto, video of PDF verwijderen uit dit moment?", { title: "Media verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    try {
      await api.delete(`/trips/${tripId}/log/${e.id}/photos/${pid}`);
      toast.success("Verwijderd uit dit moment");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
  <div className="bg-card rounded-2xl p-4" data-testid={`log-entry-${e.id}`}>
    <EntryMedia e={e} onRemove={editing ? removePhoto : null} mediaUrl={mediaUrl} setLb={setLb} t={t} />
    {e.video && <VideoEmbed video={e.video} testId={`entry-video-${e.id}`} />}
    {editing ? (
      <div>
        <textarea data-testid={`edit-log-input-${e.id}`} value={draft} maxLength={5000} rows={4} autoFocus
          onChange={(ev) => { setDraft(ev.target.value); autoGrow(ev.target); }}
          onFocus={(ev) => autoGrow(ev.target)}
          className="w-full bg-secondary rounded-xl px-3 py-2 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-[#B5838D] resize-none overflow-y-auto" />
        <label className="block text-[11px] text-muted-foreground mt-1">Datum & tijd van dit moment
          <input type="datetime-local" data-testid={`edit-log-ts-${e.id}`} value={draftTs} onChange={(ev) => setDraftTs(ev.target.value)}
            className="block w-full sm:w-auto mt-0.5 bg-secondary rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-[#B5838D]" />
        </label>
        <label className="block text-[11px] text-muted-foreground mt-1">Vlog-link (YouTube, Google Drive of Vimeo — verborgen/unlisted werkt ook)
          <input type="url" data-testid={`edit-log-video-${e.id}`} value={draftVideo} onChange={(ev) => setDraftVideo(ev.target.value)}
            placeholder="https://youtu.be/..." className="block w-full mt-0.5 bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#B5838D]" />
        </label>
        <div className="flex items-center gap-2 mt-2">
          <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" multiple hidden onChange={(ev) => { const arr = [...ev.target.files]; ev.target.value = ""; addPhotos(arr); }} />
          <button data-testid={`edit-log-add-photo-${e.id}`} onClick={() => fileRef.current?.click()} disabled={uploading} title="Foto, video of PDF toevoegen aan dit moment"
            className="rounded-full bg-secondary hover:bg-secondary/70 text-xs font-semibold px-3.5 py-1.5 flex items-center gap-1.5 disabled:opacity-50 transition-[background-color] duration-200">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} Foto/video/PDF
          </button>
          <VoiceRecorder compact onRecorded={dictate} />
          <span className="text-[10px] text-muted-foreground">dicteer met je stem</span>
        </div>
        <div className="mt-2">
          <UploadPausedBanner paused={paused} testId={`upload-paused-entry-${e.id}`} />
        </div>
        <div className="flex gap-2 mt-1.5">
          <button data-testid={`edit-log-save-${e.id}`} onClick={saveEdit} disabled={saving}
            className="rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 flex items-center gap-1 disabled:opacity-50 active:scale-95 transition-[transform] duration-200">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Opslaan
          </button>
          <button data-testid={`edit-log-cancel-${e.id}`} onClick={() => setEditing(false)}
            className="rounded-full bg-secondary text-xs font-semibold px-4 py-1.5 flex items-center gap-1 transition-[background-color] duration-200 hover:bg-secondary/70">
            <X className="w-3 h-3" /> Annuleer
          </button>
        </div>
      </div>
    ) : (
      e.text && <p className="text-sm leading-relaxed whitespace-pre-line break-words">{tr?.entries?.[e.id] || e.text}{e.edited_at && <span className="text-[10px] text-muted-foreground ml-1.5">(bewerkt)</span>}</p>
    )}
    {(!readOnly || entryReactions.length > 0) && (
      <EntryReactions reactions={entryReactions} entryId={e.id} myEmail={user?.email}
        onToggle={!readOnly ? async (emoji) => { await api.post(`/trips/${tripId}/reactions`, { entry_id: e.id, emoji }); onRefresh?.(); } : null} />
    )}
    <div className="flex items-center justify-between mt-2.5">
      <p className="text-xs text-muted-foreground font-mono flex items-center gap-1">
        {e.source === "voice" && <Mic className="w-3 h-3" />}
        {e.location_name && `${e.location_name} · `}
        {new Date(e.timestamp).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
      </p>
      {!readOnly && (
        <span className="flex items-center gap-0.5">
          <button data-testid={`edit-log-${e.id}`} onClick={() => { setDraft(e.text || ""); setDraftTs(toLocalInput(e.timestamp)); setDraftVideo(e.video?.url || ""); setEditing(true); }} title="Bewerk dit moment"
            className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button data-testid={`delete-log-${e.id}`} onClick={async () => { await api.delete(`/trips/${tripId}/log/${e.id}`); onRefresh?.(); }}
            className="p-1.5 rounded-full hover:bg-red-50 text-red-400 transition-[background-color] duration-200">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </span>
      )}
    </div>
    <DayComments comments={entryComments(e.id)} reactions={commentReactions} idKey={`e${e.id}`} myEmail={user?.email}
      placeholder="Reageer op dit moment..." t={t} locale={locale}
      onSubmit={!readOnly ? submitComment(e.day_number ?? dayNum, e.id) : null}
      onDelete={!readOnly ? deleteComment : null}
      onReact={!readOnly ? reactComment : null} />
  </div>
  );
};

export const TripStory = ({ trip, onRefresh, readOnly, shareToken, onSelectPoint, openAllComments, tr = null, t = tNL, locale = "nl-BE" }) => {
  const [generating, setGenerating] = useState(false);
  const [lb, setLb] = useState(null);
  const { user } = useAuth() || {};
  const reactions = trip.reactions || [];
  const story = trip.story;
  const logs = trip.log_entries || [];
  const days = trip.days || [];
  const topCurveDay = (() => {
    if (!(trip.travel_mode === "moto" || trip.route_preference === "bochtig")) return null;
    const cands = days.filter((d) => d.type !== "stay" && d.curviness != null && (d.curve_stars || 0) >= 2);
    if (cands.length < 2) return null;
    return cands.reduce((a, b) => (b.curviness > a.curviness ? b : a)).day_number;
  })();
  const suffix = shareToken ? `?share=${shareToken}` : "";
  const mediaUrl = (id) => `${API_BASE}/trips/${trip.id}/photos/${id}${suffix}`;
  const collage = logs.flatMap((e) => [
    ...(e.photo_id ? [e.photo_id] : []),
    ...(e.media || []).filter((m) => m.content_type?.startsWith("image")).map((m) => m.id),
  ]);

  const groups = days.map((d) => ({ day: d, entries: [] }));
  const other = [];
  logs.forEach((e) => {
    let g = e.day_number != null ? groups.find((x) => x.day.day_number === e.day_number) : null;
    if (!g) g = groups.find((x) => x.day.date === (e.timestamp || "").slice(0, 10));
    (g ? g.entries : other).push(e);
  });
  groups.forEach((g) => g.entries.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || "")));
  const comments = trip.comments || [];
  const commentReactions = trip.comment_reactions || [];
  const dayComments = (n) => comments.filter((c) => c.day_number === n && !c.entry_id);
  const entryComments = (eid) => comments.filter((c) => c.entry_id === eid);
  const submitComment = (dayNumber, entryId) => async (text, parentId) => {
    await api.post(`/trips/${trip.id}/comments`, { text, day_number: dayNumber ?? null, entry_id: entryId || null, parent_id: parentId || null });
    onRefresh?.();
  };
  const deleteComment = async (cid) => { await api.delete(`/trips/${trip.id}/comments/${cid}`); onRefresh?.(); };
  const reactComment = async (cid, emoji) => { await api.post(`/trips/${trip.id}/comment-reactions`, { comment_id: cid, emoji }); onRefresh?.(); };
  const moveComment = async (cid, entryId) => {
    await api.patch(`/trips/${trip.id}/comments/${cid}/move`, { entry_id: entryId });
    onRefresh?.();
  };
  const entryLabel = (e) => {
    const t = new Date(e.timestamp).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" });
    const txt = (e.text || "").trim();
    return `${t} — ${txt ? (txt.length > 48 ? `${txt.slice(0, 48)}…` : txt) : "moment zonder tekst"}`;
  };
  const visibleGroups = readOnly ? groups.filter((g) => g.entries.length > 0 || dayComments(g.day.day_number).length > 0) : groups;

  const pad2 = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    if (days.some((d) => d.date === todayStr)) days.forEach((d) => { if (d.date !== todayStr) init[d.day_number] = true; });
    return init;
  });
  const toggleDay = (n) => setCollapsed((c) => ({ ...c, [n]: !c[n] }));
  const jumpToDay = (n) => {
    if (n != null) setCollapsed((c) => ({ ...c, [n]: false }));
    setTimeout(() => {
      const sel = n != null ? `[data-testid="journal-day-${n}"]` : `[data-testid="journal-other-entries"]`;
      document.querySelector(sel)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };
  const allCollapsed = visibleGroups.length > 0 && visibleGroups.every((g) => collapsed[g.day.day_number]);
  const toggleAll = () => {
    const next = {};
    if (!allCollapsed) visibleGroups.forEach((g) => { next[g.day.day_number] = true; });
    setCollapsed(next);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/trips/${trip.id}/story`);
      toast.success("Reisverslag gegenereerd op basis van je reislog!");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const dayLabel = (day) => day.type === "stay" ? t("stayIn", { place: day.end?.name }) : `${day.start?.name} → ${day.end?.name}`;
  const entryCardProps = { tripId: trip.id, reactions, commentReactions, user, t, tr, locale, readOnly, onRefresh, submitComment, deleteComment, reactComment, entryComments, mediaUrl, setLb };

  return (
    <div className="space-y-6" data-testid="story-tab">
      {lb && <Lightbox urls={lb.urls} index={lb.index} onClose={() => setLb(null)} />}
      {!readOnly && <InviteCard trip={trip} onRefresh={onRefresh} />}
      {!readOnly && <AllCommentsPanel trip={trip} defaultOpen={openAllComments} onJump={jumpToDay} />}

      <div data-testid="trip-log-list">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> {t("logTitle")}
          </p>
          {visibleGroups.length > 1 && (
            <button data-testid="journal-toggle-all" onClick={toggleAll}
              className="text-[11px] font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1 shrink-0 transition-[color] duration-200">
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${allCollapsed ? "-rotate-90" : ""}`} />
              {allCollapsed ? t("expandAll") : t("collapseAll")}
            </button>
          )}
        </div>
        {!readOnly && <p className="text-xs text-muted-foreground mb-4">Voeg per dag zoveel momenten toe als je wil: notities, ingesproken verhalen, foto's en video's. Klik op een dag om die plek op de kaart te zien.</p>}
        {!readOnly && days.length > 0 && <SmartPhotoImport trip={trip} onRefresh={onRefresh} />}

        <div className="space-y-5">
          {visibleGroups.map(({ day, entries }, gi) => {
            const isCollapsed = !!collapsed[day.day_number];
            const cmtCount = comments.filter((c) => c.day_number === day.day_number).length;
            return (
            <div key={day.day_number} className="relative pl-10" data-testid={`journal-day-${day.day_number}`}>
              <div className="absolute left-0 top-0.5 w-7 h-7 rounded-full bg-[#B5838D] text-white flex items-center justify-center font-heading font-bold text-xs shadow">
                {day.day_number}
              </div>
              {gi < visibleGroups.length - 1 && <div className="absolute left-[13px] top-8 bottom-[-20px] w-px bg-border" />}

              <div className="flex items-start justify-between gap-2">
                <button data-testid={`journal-day-header-${day.day_number}`} onClick={() => day.end?.lat && onSelectPoint?.({ lat: day.end.lat, lon: day.end.lon })}
                  className="text-left group flex-1 min-w-0">
                  <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">{day.date}</p>
                  <p className="font-heading font-bold text-sm flex items-center gap-1.5 group-hover:text-[#B5838D] transition-[color] duration-200">
                    {day.type === "stay" && <Palmtree className="w-3.5 h-3.5 text-accent" />}
                    {dayLabel(day)}
                    {topCurveDay === day.day_number && (
                      <span data-testid={`journal-fun-stage-badge-${day.day_number}`}
                        title="De bochtigste dag van de trip — de leukste etappe!"
                        className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-600 px-2 py-0.5 text-[10px] font-bold">
                        <Trophy className="w-3 h-3" /> leukste etappe
                      </span>
                    )}
                    <MapPin className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity duration-200" />
                  </p>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {topCurveDay === day.day_number && <StageShareButton trip={trip} day={day} testId={`share-stage-journal-${day.day_number}`} />}
                  <button data-testid={`journal-day-toggle-${day.day_number}`} onClick={() => toggleDay(day.day_number)} title={isCollapsed ? "Dag uitklappen" : "Dag inklappen"}
                    className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground shrink-0 transition-[background-color] duration-200">
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`} />
                  </button>
                </div>
              </div>

              {isCollapsed ? (
                (entries.length > 0 || cmtCount > 0) && (
                  <button data-testid={`journal-day-summary-${day.day_number}`} onClick={() => toggleDay(day.day_number)}
                    className="mt-1.5 text-xs text-muted-foreground hover:text-foreground font-mono transition-[color] duration-200">
                    {[entries.length > 0 ? `${entries.length} ${entries.length === 1 ? t("momentSing") : t("momentPlur")}` : null, cmtCount > 0 ? `${cmtCount} ${cmtCount === 1 ? t("reactSing") : t("reactPlur")}` : null].filter(Boolean).join(" · ")}
                  </button>
                )
              ) : (
                <>
                  {entries.length > 0 && (
                    <div className="space-y-2.5 mt-3">
                      {entries.map((e) => <EntryCard key={e.id} e={e} dayNum={day.day_number} {...entryCardProps} />)}
                    </div>
                  )}
                  {!readOnly && <DayComposer trip={trip} day={day} onRefresh={onRefresh} defaultOpen={day.date === todayStr} />}
                  {dayComments(day.day_number).length > 0 && (
                    <DayComments comments={dayComments(day.day_number)} reactions={commentReactions} idKey={day.day_number}
                      myEmail={user?.email} label={t("dayCommentsLabel")} replyOnly={entries.length > 0} t={t} locale={locale}
                      onSubmit={!readOnly ? submitComment(day.day_number, null) : null}
                      onDelete={!readOnly ? deleteComment : null}
                      onReact={!readOnly ? reactComment : null}
                      onMove={!readOnly ? moveComment : null}
                      moveTargets={entries.map((e) => ({ id: e.id, label: entryLabel(e) }))} />
                  )}
                </>
              )}
            </div>
            );
          })}
        </div>

        {other.length > 0 && (
          <div className="mt-6" data-testid="journal-other-entries">
            <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">{t("otherMoments")}</p>
            <div className="space-y-2.5">
              {[...other].reverse().map((e) => <EntryCard key={e.id} e={e} {...entryCardProps} />)}
            </div>
          </div>
        )}

        {logs.length === 0 && readOnly && (
          <div className="bg-card rounded-2xl p-6 text-center">
            <p className="text-sm text-muted-foreground">{t("noLogYet")}</p>
          </div>
        )}
      </div>

      <div data-testid="story-section">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-accent" /> {t("storyHeader")}
        </p>
        {!story && !readOnly && (
          <div className="bg-card rounded-2xl p-8 text-center">
            <Sparkles className="w-8 h-8 text-accent mx-auto mb-3" />
            <p className="font-heading font-bold mb-1">AI-reisverslag</p>
            <p className="text-sm text-muted-foreground mb-4">AI schrijft een deelbaar reisverhaal op basis van jouw reislog hierboven — Polarsteps-stijl. Hoe meer je vastlegt, hoe persoonlijker het verhaal.</p>
            <Button data-testid="story-generate-button" onClick={generate} disabled={generating} className="rounded-full bg-primary hover:bg-[#1B4332]">
              {generating ? "Schrijven..." : "Genereer reisverslag"}
            </Button>
          </div>
        )}
        {story && (
          <article className="bg-card rounded-2xl p-6 sm:p-8" data-testid="story-article">
            <h2 className="font-heading font-extrabold text-2xl tracking-tight mb-3">{tr?.story?.title || story.title}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5 italic">{tr?.story?.intro || story.intro}</p>
            {collage.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                {collage.slice(0, 6).map((id) => (
                  <img key={id} src={mediaUrl(id)} alt="Tripfoto" className="rounded-xl h-28 w-full object-cover" />
                ))}
              </div>
            )}
            <div className="space-y-4">
              {story.days?.map((d) => (
                <div key={d.day}>
                  <p className="text-xs tracking-[0.15em] uppercase font-semibold text-primary mb-1">{t("day")} {d.day}</p>
                  <p className="text-sm leading-relaxed">{tr?.story?.days?.[String(d.day)] || d.text}</p>
                </div>
              ))}
            </div>
            {story.outro && <p className="text-sm leading-relaxed mt-5 font-medium">{tr?.story?.outro || story.outro}</p>}
            {!readOnly && (
              <button data-testid="story-regenerate-button" onClick={generate} disabled={generating}
                className="mt-6 text-xs font-semibold text-primary hover:text-[#1B4332] flex items-center gap-1 transition-[color] duration-200">
                <RefreshCw className={`w-3 h-3 ${generating ? "animate-spin" : ""}`} /> {generating ? "Schrijven..." : "Opnieuw genereren (o.b.v. je reislog)"}
              </button>
            )}
          </article>
        )}
      </div>

      {(trip.feedback || []).length > 0 && (
        <div className="space-y-3" data-testid="trip-feedback-list">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2">
            <MessageCircleHeart className="w-4 h-4 text-[#B5838D]" /> {t("familyReactions")} ({trip.feedback.length})
          </p>
          {[...trip.feedback].reverse().map((f) => (
            <div key={f.id} className="bg-[#B5838D]/5 rounded-2xl p-4">
              <p className="text-sm leading-relaxed">{f.text}</p>
              <p className="text-xs text-muted-foreground mt-2 font-semibold flex items-center gap-1">
                {f.source === "voice" && <Mic className="w-3 h-3" />} {f.author_name} · <span className="font-mono font-normal">{new Date(f.created_at).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
